import {
  BadgeAlert,
  BookMarked,
  ChartLine,
  ChevronRight,
  Coffee,
  LayoutDashboard,
  LogOut,
  MessageSquareQuote,
  Megaphone,
  Radio,
  ScrollText,
  Settings,
  ShieldCheck,
  UserRoundCheck,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";

import { ConfirmDialog, Logo } from "@/components/shared";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { PATHS, SIDEBAR_PERMISSION } from "@/constants";
import { useCan, useLogout } from "@/hooks";
import { cn } from "@/lib/utils";

interface NavChild {
  to: string;
  labelKey: string;
  /** O'z ruxsati: ota band ochilsa ham, ruxsatsiz bola ko'rinmaydi. */
  permission: string;
}

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /**
   * Bandni ko'rsatish uchun yetarli SAHIFA ruxsati.
   *
   * Bolali bandda bu ENG KENG ruxsat: `payments.read` bor, lekin
   * `ledger.read` yo'q odam "Pul" bo'limini ko'rishi va ichidan
   * to'lovlarni ochishi kerak. Bolalar o'zlarini alohida tekshiradi.
   */
  permission: string | string[];
  /**
   * Menyudagi bandning O'Z ruxsati (`sidebar.*`).
   *
   * Sahifa ruxsatidan ALOHIDA va u bilan BIRGA tekshiriladi.
   * Ikkitasining ma'nosi boshqa: sahifa ruxsati "kira oladimi" ni,
   * menyu ruxsati esa "kundalik ish o'rnida shu band tursinmi" ni
   * hal qiladi. Moderator ma'lumotnomalar sahifasiga havola bo'yicha
   * kirishi mumkin, lekin uning menyusida faqat shikoyatlar turishi
   * kerak.
   *
   * VA (AND), YOKI (OR) EMAS: faqat menyu ruxsatiga qarasak, sahifaga
   * kira olmaydigan odamga havola ko'rsatilardi — bosgach "ruxsat
   * yo'q" chiqardi, va bu menyuning o'zini yolg'onga aylantirardi.
   */
  sidebarPermission: string;
  end?: boolean;
  /** Bola bandlar. Bo'lsa, ota band havola emas — ochiluvchi bo'lim. */
  children?: NavChild[];
}

/**
 * Menyu — bitta tekis ro'yxat.
 *
 * Ilgari u beshta sarlavhali guruhga bo'lingan edi ("Umumiy",
 * "Odamlar", "Pul", ...). O'n bir band uchun beshta sarlavha ko'p:
 * sarlavhalar bandlarning yarmicha joy egallab, ro'yxatni ikki
 * barobar uzaytirardi va yig'ilgan menyuda umuman ko'rinmasdi.
 * Bir-biriga tegishli bandlar endi ochiluvchi bo'limga yig'iladi —
 * bu sarlavhadan farqli o'laroq, joyni QAYTARADI.
 *
 * Har bir element o'z RUXSATI bilan (ADR 0009): odam faqat o'zi kira
 * oladigan bo'limlarni ko'radi. Bu — qulaylik, xavfsizlik emas.
 * Menyudan yashirilgan sahifaga manzil orqali kirish mumkin, va uni
 * backend to'xtatadi.
 */
export const NAV: NavItem[] = [
  {
    to: PATHS.DASHBOARD,
    labelKey: "nav.dashboard",
    icon: LayoutDashboard,
    permission: "dashboard.read",
    sidebarPermission: SIDEBAR_PERMISSION.DASHBOARD,
    end: true,
  },
  {
    to: PATHS.ANALYTICS,
    labelKey: "nav.analytics",
    icon: ChartLine,
    permission: "analytics.read",
    sidebarPermission: SIDEBAR_PERMISSION.ANALYTICS,
  },
  {
    to: PATHS.USERS,
    labelKey: "nav.users",
    icon: Users,
    permission: "users.read",
    sidebarPermission: SIDEBAR_PERMISSION.USERS,
  },
  {
    to: PATHS.INVITATIONS,
    labelKey: "nav.invitations",
    icon: Coffee,
    permission: "invitations.read",
    sidebarPermission: SIDEBAR_PERMISSION.INVITATIONS,
  },
  {
    // Uchta ro'yxat bitta narsaning uch ko'rinishi: qahva harakati,
    // pulning kirishi va pulning chiqishi. Birinchi qavatda uchta
    // band bo'lib turgani menyuni cho'zardi, holbuki admin ular
    // orasida kunda bir-ikki marta yuradi.
    to: PATHS.FINANCE,
    labelKey: "nav.finance",
    icon: Wallet,
    permission: ["ledger.read", "payments.read", "promo.read", "payouts.read"],
    sidebarPermission: SIDEBAR_PERMISSION.FINANCE,
    children: [
      { to: PATHS.LEDGER, labelKey: "nav.ledger", permission: "ledger.read" },
      { to: PATHS.PAYMENTS, labelKey: "nav.payments", permission: "payments.read" },
      { to: PATHS.PROMO, labelKey: "nav.promo", permission: "promo.read" },
      // "Chiqarishlar" va "Soliq profillari" MENYUDAN OLIB TASHLANDI
      // (ADR 0025): qahva pulga chiqarilmaydi, ya'ni ikkala navbat
      // ham hech qachon to'lmaydi. Har kuni ochiladigan menyuda doim
      // bo'sh turadigan ikki punkt — moderatorga har kuni beriladigan
      // ikki ortiqcha savol.
      //
      // Marshrutlarning o'zi QOLDI: qaror kutayotgan eski so'rovlar
      // bo'lishi mumkin va ularni yopadigan boshqa joy yo'q. Boshqaruv
      // panelidagi navbat kartochkasi ham o'sha yerga olib boradi —
      // lekin u faqat son noldan katta bo'lganda ko'rinadi.
    ],
  },
  {
    // Efirlar (ADR 0023) moliyadan OLDIN emas, keyin: efir marketing
    // quroli va admin unga oyiga bir-ikki marta kiradi, pul
    // ro'yxatlariga esa har kuni.
    to: PATHS.LIVE,
    labelKey: "nav.live",
    icon: Radio,
    permission: "live.manage",
    sidebarPermission: SIDEBAR_PERMISSION.LIVE,
  },
  {
    to: PATHS.REPORTS,
    labelKey: "nav.reports",
    icon: BadgeAlert,
    permission: "reports.read",
    sidebarPermission: SIDEBAR_PERMISSION.REPORTS,
  },
  {
    to: PATHS.REVIEWS,
    labelKey: "nav.reviews",
    icon: MessageSquareQuote,
    permission: "reviews.read",
    sidebarPermission: SIDEBAR_PERMISSION.REVIEWS,
  },
  {
    to: PATHS.MESSAGES,
    labelKey: "nav.messages",
    icon: Megaphone,
    permission: "notifications.send",
    sidebarPermission: SIDEBAR_PERMISSION.MESSAGES,
  },
  {
    to: PATHS.HOST_APPLICATIONS,
    labelKey: "nav.hostApplications",
    icon: UserRoundCheck,
    permission: "hosts.applications.read",
    sidebarPermission: SIDEBAR_PERMISSION.HOST_APPLICATIONS,
  },
  {
    to: PATHS.ROLES,
    labelKey: "nav.roles",
    icon: ShieldCheck,
    permission: "roles.read",
    sidebarPermission: SIDEBAR_PERMISSION.ROLES,
  },
  {
    // Audit jurnali rollar bilan yonma-yon: ikkalasi ham "kim nima
    // qila oladi" va "kim nima qildi" degan bitta savolning ikki
    // yarmi.
    to: PATHS.AUDIT,
    labelKey: "nav.audit",
    icon: ScrollText,
    permission: "audit.read",
    sidebarPermission: SIDEBAR_PERMISSION.AUDIT,
  },
  {
    to: PATHS.REFERENCE,
    labelKey: "nav.reference",
    icon: BookMarked,
    permission: "reference.read",
    sidebarPermission: SIDEBAR_PERMISSION.REFERENCE,
    children: [
      {
        to: PATHS.REFERENCE_INDUSTRIES,
        labelKey: "nav.referenceIndustries",
        permission: "reference.read",
      },
      {
        to: PATHS.REFERENCE_REGIONS,
        labelKey: "nav.referenceRegions",
        permission: "reference.read",
      },
      {
        to: PATHS.REFERENCE_DISTRICTS,
        labelKey: "nav.referenceDistricts",
        permission: "reference.read",
      },
    ],
  },
  {
    // Ikki sahifa bitta ruxsat ostida, lekin butunlay boshqa ish:
    // biri raqamlar formasi, ikkinchisi variantlar ro'yxati. Ularni
    // bitta sahifaga yig'ish "nima saqlandi?" degan savolni
    // tug'dirardi — saqlash modeli ham boshqacha.
    to: PATHS.SETTINGS,
    labelKey: "nav.settings",
    icon: Settings,
    permission: "settings.read",
    sidebarPermission: SIDEBAR_PERMISSION.SETTINGS,
    children: [
      {
        to: PATHS.SETTINGS_PLATFORM,
        labelKey: "nav.settingsPlatform",
        permission: "settings.read",
      },
      {
        to: PATHS.SETTINGS_MEETINGS,
        labelKey: "nav.settingsMeetings",
        permission: "settings.read",
      },
    ],
  },
];

/**
 * Yig'ilgan menyuda bo'lim: bosilganda ro'yxat chiqadi.
 *
 * Yig'ilgan holatda ichki bandlar KO'RINMAYDI — ular gorizontal
 * joy talab qiladi, va u joy aynan yo'q. Ochilish-yopilishni
 * qoldirsak, bosilgan tugma hech narsa qilmagandek tuyulardi va
 * "Hisob", "To'lovlar", "Chiqarishlar" umuman ochilmay qolardi:
 * ilgari ular menyuning birinchi qavatida turgan, ya'ni bu tuzatish
 * emas, YO'QOTISHNING oldini olish.
 */
function CollapsedNavGroup({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton tooltip={t(item.labelKey)} isActive={pathname.startsWith(item.to)}>
            <item.icon className="size-4" />
            <span>{t(item.labelKey)}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent side="right" align="start" className="min-w-44">
          {/* Sarlavha kerak: yig'ilgan menyuda tugmaning yonida
              bo'lim nomi yozilmagan, va ro'yxat kontekstsiz
              ochilardi. */}
          <DropdownMenuLabel>{t(item.labelKey)}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {item.children?.map(child => (
            <DropdownMenuItem key={child.to} asChild>
              <NavLink to={child.to}>{t(child.labelKey)}</NavLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
}

/**
 * Bolalari bor band: ochiluvchi bo'lim.
 *
 * Joriy manzil ichkarida bo'lsa o'zi ochilib turadi — foydalanuvchi
 * qaysi sahifada turganini ko'rish uchun menyuni qo'lda ochishi kerak
 * bo'lmasin. Undan keyin ochiq-yopiqligi uning o'z qaroriga qoladi.
 */
function NavGroupItem({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { state, isMobile } = useSidebar();
  const insideSection = pathname.startsWith(item.to);
  const [open, setOpen] = useState(insideSection);

  // Mobil menyu yig'ilmaydi — u to'liq kenglikda ochiladi, ya'ni
  // ichki bandlar joyida ko'rinadi.
  if (state === "collapsed" && !isMobile) return <CollapsedNavGroup item={item} />;

  const expanded = open || insideSection;

  return (
    <>
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={t(item.labelKey)}
          isActive={insideSection}
          onClick={() => setOpen(previous => !previous)}
          aria-expanded={expanded}
        >
          <item.icon className="size-4" />
          <span>{t(item.labelKey)}</span>
          <ChevronRight
            className={cn("ml-auto size-4 transition-transform", expanded && "rotate-90")}
          />
        </SidebarMenuButton>
      </SidebarMenuItem>

      {expanded && (
        <SidebarMenuSub>
          {item.children?.map(child => (
            <SidebarMenuSubItem key={child.to}>
              <NavLink to={child.to}>
                {({ isActive }) => (
                  <SidebarMenuSubButton asChild isActive={isActive}>
                    <span>{t(child.labelKey)}</span>
                  </SidebarMenuSubButton>
                )}
              </NavLink>
            </SidebarMenuSubItem>
          ))}
        </SidebarMenuSub>
      )}
    </>
  );
}

export function AppSidebar() {
  const { t } = useTranslation();
  const { can, canAny } = useCan();
  const { signOut, isPending } = useLogout();
  const [confirming, setConfirming] = useState(false);

  // Ikkita shart BIRGA: menyu ruxsati bandni ko'rsatadi, sahifa
  // ruxsati esa uning ortidagi sahifani ochadi. Faqat birinchisiga
  // qarasak, kira olmaydigan sahifaga havola chiqib qolardi — bosgach
  // "ruxsat yo'q" degan ekran, ya'ni menyuning o'zi yolg'on bo'lardi.
  const items = NAV.filter(
    item =>
      can(item.sidebarPermission) &&
      (Array.isArray(item.permission) ? canAny(item.permission) : can(item.permission)),
  ).map(item => ({
    ...item,
    children: item.children?.filter(child => can(child.permission)),
  }));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-16 justify-center px-3">
        <Logo />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map(item =>
                item.children?.length ? (
                  <NavGroupItem key={item.to} item={item} />
                ) : (
                  <SidebarMenuItem key={item.to}>
                    {/* `NavLink` faol holatni o'zi hisoblaydi — joriy
                        manzilni qo'lda solishtirish kerak emas. */}
                    <NavLink to={item.to} end={item.end ?? false}>
                      {({ isActive }) => (
                        <SidebarMenuButton asChild isActive={isActive} tooltip={t(item.labelKey)}>
                          <span>
                            <item.icon className="size-4" />
                            <span>{t(item.labelKey)}</span>
                          </span>
                        </SidebarMenuButton>
                      )}
                    </NavLink>
                  </SidebarMenuItem>
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Chiqish menyuning ENG PASTIDA: u navigatsiya emas, va
          bandlar orasida turgani "yana bitta sahifa" degan
          taassurot qoldirardi. Sarlavhadagi menyuda ham qoladi —
          odamlar uni ikkala joydan qidiradi. */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              // Espresso fonda `destructive` qizili xira ko'rinadi —
              // och atirgul ohangi o'qiladi va baribir "xavfli" deydi.
              className="text-tile-berry hover:bg-tile-berry-strong/20 hover:text-tile-berry"
              tooltip={t("common.logout")}
              onClick={() => setConfirming(true)}
            >
              <LogOut className="size-4" />
              <span>{t("common.logout")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t("common.logoutTitle")}
        description={t("common.logoutBody")}
        confirmLabel={t("common.logout")}
        loading={isPending}
        onConfirm={() => void signOut()}
      />
    </Sidebar>
  );
}
